import { useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { formatDutyRate, type BrokerAnswerRow, type BrokerOpenPositionDto } from '@crm/shared'
import { bindBrokerAnswer } from '../../../shared/api/customs.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { formatCustomsFee } from './brokerColDefs'

/**
 * Строка итога загрузки, которую логист привязывает сам (просьба Сергея 30.10.2026):
 * слева — партномер, как его записал брокер, и кратко его ответ; справа —
 * выпадающий список позиций запросов этого файла без ответа. «Сохранить» пишет
 * ответ брокера на выбранную позицию.
 */
export function BrokerBindRow({
  brokerPart,
  row,
  options,
  preselect,
  tone,
  onBound,
  fallback,
}: {
  brokerPart: string
  row: BrokerAnswerRow | null
  /** Позиции без ответа (уже привязанные в этом окне убраны). */
  options: BrokerOpenPositionDto[]
  preselect?: string
  tone?: 'warn'
  onBound: (itemId: string) => void
  /** Запасной поиск по всем запросам брокеру. */
  fallback?: ReactNode
}) {
  const qc = useQueryClient()
  const [picked, setPicked] = useState(() =>
    preselect && options.some((o) => o.itemId === preselect) ? preselect : '',
  )
  const [state, setState] = useState<{ busy?: boolean; done?: string; error?: string }>({})
  const [searching, setSearching] = useState(false)
  const choice = options.find((o) => o.itemId === picked) ?? null

  const save = async () => {
    if (!row || !choice) return
    setState({ busy: true })
    try {
      const res = await bindBrokerAnswer({
        itemId: choice.itemId,
        brokerRequestId: choice.brokerRequestId,
        row,
      })
      setState({ done: `Записано на ${res.partNumber} — «Ответ получен»` })
      onBound(choice.itemId)
      void qc.invalidateQueries({ queryKey: ['customs'] })
    } catch (err) {
      setState({ error: apiErrorText(err, 'Не удалось записать ответ') })
    }
  }

  const frame =
    state.done != null
      ? 'border-green-200 bg-green-50/40'
      : tone === 'warn'
        ? 'border-amber-200 bg-amber-50/40'
        : 'border-gray-200'

  return (
    <li className={`rounded-lg border p-2 space-y-1.5 ${frame}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        <div className="min-w-0">
          <div className="text-gray-500">Брокер ответил по партномеру</div>
          <div className="font-medium text-sm break-words">{brokerPart}</div>
          {row && <div className="text-gray-600 mt-0.5 break-words">{answerSummary(row)}</div>}
        </div>
        <div className="min-w-0">
          {state.done ? (
            <div className="text-green-700 font-medium">{state.done}</div>
          ) : !row ? (
            <div className="text-gray-500">Строки нет в файле — записать нельзя</div>
          ) : (
            <>
              <label className="text-gray-500 block">
                Записать ответ на позицию (из этого запроса, без ответа)
                <select
                  value={picked}
                  onChange={(e) => setPicked(e.target.value)}
                  aria-label={`Позиция для ${brokerPart}`}
                  className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded-lg text-xs text-gray-800 bg-white"
                >
                  <option value="">
                    {options.length === 0 ? 'Позиций без ответа нет' : 'Выберите партномер…'}
                  </option>
                  {options.map((o) => (
                    <option key={o.itemId} value={o.itemId}>
                      {optionLabel(o)}
                    </option>
                  ))}
                </select>
              </label>
              {choice?.sentPartNumber && (
                <div className="mt-0.5 text-amber-700">
                  брокеру ушёл: {choice.sentPartNumber} (менеджер поправил)
                </div>
              )}
              {fallback && (
                <div className="mt-1 text-gray-500">
                  Нет в списке?{' '}
                  <button
                    type="button"
                    onClick={() => setSearching((v) => !v)}
                    className="underline hover:text-gray-700"
                  >
                    Искать по всем запросам брокеру
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {!state.done && row && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => void save()}
            disabled={!choice || state.busy}
            className="px-3 py-1 bg-brand-600 text-white text-xs rounded-lg hover:bg-brand-700 disabled:opacity-50"
          >
            {state.busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      )}
      {state.error && <div className="text-red-600">{state.error}</div>}
      {searching && !state.done && fallback && <ul className="space-y-1.5">{fallback}</ul>}
    </li>
  )
}

function optionLabel(o: BrokerOpenPositionDto): string {
  const parts = [o.partNumber, o.brand, o.requestNumber ? `заявка ${o.requestNumber}` : null]
  const head = parts.filter(Boolean).join(' · ')
  return o.sentPartNumber ? `${head} (брокеру ушёл: ${o.sentPartNumber})` : head
}

/** Кратко, что ответил брокер: ТН ВЭД, пошлина, нетарифка, сбор. */
export function answerSummary(row: BrokerAnswerRow): string {
  const out: string[] = []
  if (row.tnvedCode?.trim()) out.push(`ТН ВЭД ${row.tnvedCode.trim()}`)
  if (row.dutyRate != null) out.push(`пошлина ${formatDutyRate(row.dutyRate)}`)
  const note = row.nonTariffNote?.trim()
  if (note) out.push(`нетарифка: ${note.length > 80 ? `${note.slice(0, 80)}…` : note}`)
  if (row.customsFee != null) out.push(`сбор: ${formatCustomsFee(row.customsFee)}`)
  return out.join(' · ') || 'ответ без ТН ВЭД и пошлины'
}
